import React, { useState, useEffect, useCallback } from 'react'
import {ArrowRight, TrendingUp, Shield, Zap, Star, Search, DollarSign} from 'lucide-react'
import { useAuth } from './hooks/useAuth'
import { useAdminAuth } from './hooks/useAdminAuth'
import { useCommenterAuth } from './hooks/useCommenterAuth'
import { useProjects, Project } from './hooks/useProjects'
import { Dashboard } from './components/Dashboard'
import { ExplorePage } from './components/ExplorePage'
import { ProjectProfilePage } from './components/ProjectProfilePage'
import { Header } from './components/Header'
import { AdminLogin } from './components/AdminLogin'
import { AdminPanel } from './components/AdminPanel'
import { FixChumpProject } from './components/FixChumpProject'
import { Footer } from './components/Footer'
import { HowItWorksPage } from './components/HowItWorksPage'
import { TermsPage } from './components/TermsPage'
import { PrivacyPage } from './components/PrivacyPage'
import { PricingPage } from './components/PricingPage'
import { UserLogin } from './components/UserLogin'
import { UserProfilePage } from './components/UserProfilePage'
import { NewsFeedPage } from './components/NewsFeedPage'
import { SearchUsersPage } from './components/SearchUsersPage'

type View = 'home' | 'explore' | 'projectProfile' | 'dashboard' | 'admin' | 'howItWorks' | 'terms' | 'privacy' | 'pricing' | 'userProfile' | 'newsFeed' | 'searchUsers'

function App() {
  const { user, signIn, loading: authLoading } = useAuth()
  const { adminUser, isAdmin, adminSignIn, adminSignOut } = useAdminAuth()
  const { user: commenterUser, refreshUser: refreshCommenterUser } = useCommenterAuth()
  const { projects, loading: projectsLoading, fetchActiveProjects, incrementClick } = useProjects()
  const [hoveredBanner, setHoveredBanner] = useState<string | null>(null)
  const [currentView, setCurrentView] = useState<View>('home')
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [showAdminLogin, setShowAdminLogin] = useState(false)
  const [showHowItWorks, setShowHowItWorks] = useState(false)
  const [showUserLogin, setShowUserLogin] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  // Set initial view to dashboard if user is logged in, or admin panel if admin
  useEffect(() => {
    if (isAdmin && currentView === 'home') {
      setCurrentView('admin')
    } else if (user && currentView === 'home') {
      setCurrentView('dashboard')
    }
  }, [user, isAdmin, currentView])

  useEffect(() => {
    // Force refresh on mount to show latest projects
    fetchActiveProjects(true)
  }, [])

  const handleUrlRoute = useCallback(async (path: string) => {
    // Remove leading slash
    const cleanPath = path.replace(/^\//, '').replace(/\/$/, '')
    
    if (!cleanPath) return
    
    // Check if it's a user profile route
    if (cleanPath.startsWith('users/')) {
      const username = cleanPath.replace('users/', '')
      // Find user by username
      try {
        const { lumi } = await import('./lib/lumi')
        const result = await lumi.entities.userAccounts.list({
          filter: { username }
        })
        if (result.list.length > 0) {
          setSelectedUserId(result.list[0]._id)
          setCurrentView('userProfile')
        }
      } catch (error) {
        console.error('Error finding user:', error)
      }
      return
    }
    
    // Otherwise, treat as project name
    const project = projects.find(p => 
      p.projectName.toLowerCase().replace(/\s+/g, '') === cleanPath.toLowerCase()
    )
    
    if (project) {
      setSelectedProject(project)
      setCurrentView('projectProfile')
    }
  }, [projects])

  // Handle URL-based routing on load
  useEffect(() => {
    const path = window.location.pathname
    if (path && path !== '/') {
      handleUrlRoute(path)
    }
  }, [handleUrlRoute])

  // Scroll to top on view change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentView, selectedProject, selectedUserId])

  const updateUrl = (view: View, identifier?: string) => {
    if (view === 'projectProfile' && selectedProject) {
      const urlName = selectedProject.projectName.replace(/\s+/g, '')
      window.history.pushState({}, '', `/${urlName}`)
    } else if (view === 'userProfile' && identifier) {
      window.history.pushState({}, '', `/users/${identifier}`)
    } else if (view === 'home') {
      window.history.pushState({}, '', '/')
    }
  }

  const handleBannerClick = async (project: Project) => {
    await incrementClick(project._id)
    setSelectedProject(project)
    setCurrentView('projectProfile')
    // Update URL
    const urlName = project.projectName.replace(/\s+/g, '')
    window.history.pushState({}, '', `/${urlName}`)
  }

  const handleSignIn = async () => {
    try {
      await signIn()
    } catch (error: any) {
      // Silently handle auth window closed - user intentionally cancelled
      if (error?.message === 'Auth window closed') {
        console.log('User closed authentication window')
        return
      }
      // Show error for other auth failures
      console.error('Sign in error:', error)
      alert('Sign in failed. Please try again.')
    }
  }

  const handleAdminLogin = async (username: string, password: string) => {
    const success = await adminSignIn(username, password)
    if (success) {
      setShowAdminLogin(false)
      setCurrentView('admin')
    }
    return success
  }

  const handleAdminSignOut = () => {
    adminSignOut()
    setCurrentView('home')
  }

  const handleUserLoginSuccess = async () => {
    await refreshCommenterUser()
    setSelectedUserId(null)  // Clear selected user when logging in
    setCurrentView('userProfile')
    window.history.pushState({}, '', '/profile')
  }

  const handleViewOwnProfile = () => {
    setSelectedUserId(null)  // Clear selected user to view own profile
    setCurrentView('userProfile')
    window.history.pushState({}, '', '/profile')
  }

  // Show admin login if requested
  if (showAdminLogin && !isAdmin) {
    return <AdminLogin onLogin={handleAdminLogin} />
  }

  // Show admin panel view
  if (currentView === 'admin') {
    if (!isAdmin) {
      setCurrentView('home')
      return null
    }
    return <AdminPanel adminUsername={adminUser?.username || 'Admin'} onSignOut={handleAdminSignOut} />
  }

  // Show How it Works page
  if (currentView === 'howItWorks') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
        {/* User Login Modal */}
        {showUserLogin && (
          <UserLogin 
            onClose={() => setShowUserLogin(false)} 
            onSuccess={handleUserLoginSuccess}
          />
        )}
        <Header 
          currentView={currentView} 
          onNavigate={setCurrentView} 
          onViewOwnProfile={handleViewOwnProfile}
          onSignIn={handleSignIn}
          onUserLogin={() => setShowUserLogin(true)}
          user={user}
          commenterUser={commenterUser}
        />
        <HowItWorksPage onBack={() => setCurrentView('home')} />
        <Footer 
          onAdminClick={() => setShowAdminLogin(true)}
          onHowItWorksClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('howItWorks')
          }}
          onTermsClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('terms')
          }}
          onPrivacyClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('privacy')
          }}
        />
      </div>
    )
  }

  // Show Terms page
  if (currentView === 'terms') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
        {/* User Login Modal */}
        {showUserLogin && (
          <UserLogin 
            onClose={() => setShowUserLogin(false)} 
            onSuccess={handleUserLoginSuccess}
          />
        )}
        <Header 
          currentView={currentView} 
          onNavigate={setCurrentView} 
          onViewOwnProfile={handleViewOwnProfile}
          onSignIn={handleSignIn}
          onUserLogin={() => setShowUserLogin(true)}
          user={user}
          commenterUser={commenterUser}
        />
        <TermsPage onBack={() => setCurrentView('home')} />
        <Footer 
          onAdminClick={() => setShowAdminLogin(true)}
          onHowItWorksClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('howItWorks')
          }}
          onTermsClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('terms')
          }}
          onPrivacyClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('privacy')
          }}
        />
      </div>
    )
  }

  // Show Privacy page
  if (currentView === 'privacy') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
        {/* User Login Modal */}
        {showUserLogin && (
          <UserLogin 
            onClose={() => setShowUserLogin(false)} 
            onSuccess={handleUserLoginSuccess}
          />
        )}
        <Header 
          currentView={currentView} 
          onNavigate={setCurrentView} 
          onViewOwnProfile={handleViewOwnProfile}
          onSignIn={handleSignIn}
          onUserLogin={() => setShowUserLogin(true)}
          user={user}
          commenterUser={commenterUser}
        />
        <PrivacyPage onBack={() => setCurrentView('home')} />
        <Footer 
          onAdminClick={() => setShowAdminLogin(true)}
          onHowItWorksClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('howItWorks')
          }}
          onTermsClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('terms')
          }}
          onPrivacyClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('privacy')
          }}
        />
      </div>
    )
  }

  // Show Pricing page
  if (currentView === 'pricing') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
        {/* User Login Modal */}
        {showUserLogin && (
          <UserLogin 
            onClose={() => setShowUserLogin(false)} 
            onSuccess={handleUserLoginSuccess}
          />
        )}
        <Header 
          currentView={currentView} 
          onNavigate={setCurrentView} 
          onViewOwnProfile={handleViewOwnProfile}
          onSignIn={handleSignIn} 
          onUserLogin={() => setShowUserLogin(true)}
          user={user}
          commenterUser={commenterUser}
        />
        <PricingPage 
          currentUserId={user?.userId}
          user={user}
          onNavigate={setCurrentView}
          onSignIn={handleSignIn}
        />
        <Footer 
          onAdminClick={() => setShowAdminLogin(true)}
          onHowItWorksClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('howItWorks')
          }}
          onTermsClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('terms')
          }}
          onPrivacyClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('privacy')
          }}
        />
      </div>
    )
  }

  // Show User Profile page
  if (currentView === 'userProfile') {
    return (
      <>
        <UserProfilePage 
          onNavigate={setCurrentView} 
          viewingUserId={selectedUserId}
        />
      </>
    )
  }

  // Show Search Users page
  if (currentView === 'searchUsers') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
        {/* User Login Modal */}
        {showUserLogin && (
          <UserLogin 
            onClose={() => setShowUserLogin(false)} 
            onSuccess={handleUserLoginSuccess}
          />
        )}
        <Header 
          currentView={currentView} 
          onNavigate={setCurrentView} 
          onViewOwnProfile={handleViewOwnProfile}
          onSignIn={handleSignIn}
          onUserLogin={() => setShowUserLogin(true)}
          user={user}
          commenterUser={commenterUser}
        />
        <SearchUsersPage 
          onBack={() => setCurrentView('home')}
          onUserClick={async (userId) => {
            setSelectedUserId(userId)
            setCurrentView('userProfile')
            // Get username for URL
            try {
              const { lumi } = await import('./lib/lumi')
              const user = await lumi.entities.userAccounts.get(userId)
              if (user?.username) {
                window.history.pushState({}, '', `/users/${user.username}`)
              }
            } catch (error) {
              console.error('Error getting user for URL:', error)
            }
          }}
        />
        <Footer 
          onAdminClick={() => setShowAdminLogin(true)}
          onHowItWorksClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('howItWorks')
          }}
          onTermsClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('terms')
          }}
          onPrivacyClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('privacy')
          }}
        />
      </div>
    )
  }

  // Show News Feed page
  if (currentView === 'newsFeed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
        {/* User Login Modal */}
        {showUserLogin && (
          <UserLogin 
            onClose={() => setShowUserLogin(false)} 
            onSuccess={handleUserLoginSuccess}
          />
        )}
        <Header 
          currentView={currentView} 
          onNavigate={setCurrentView} 
          onViewOwnProfile={handleViewOwnProfile}
          onSignIn={handleSignIn}
          onUserLogin={() => setShowUserLogin(true)}
          user={user}
          commenterUser={commenterUser}
        />
        <NewsFeedPage 
          onBack={() => setCurrentView('home')}
          currentUserId={user?.userId || commenterUser?.userId}
          onProjectClick={(project) => {
            setSelectedProject(project)
            setCurrentView('projectProfile')
            const urlName = project.projectName.replace(/\s+/g, '')
            window.history.pushState({}, '', `/${urlName}`)
          }}
        />
        <Footer 
          onAdminClick={() => setShowAdminLogin(true)}
          onHowItWorksClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('howItWorks')
          }}
          onTermsClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('terms')
          }}
          onPrivacyClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('privacy')
          }}
        />
      </div>
    )
  }

  // Show dashboard view
  if (currentView === 'dashboard') {
    if (!user) {
      // Redirect to home if not logged in
      setCurrentView('home')
      return null
    }
    return (
      <div>
        {/* User Login Modal */}
        {showUserLogin && (
          <UserLogin 
            onClose={() => setShowUserLogin(false)} 
            onSuccess={handleUserLoginSuccess}
          />
        )}
        <Header 
          currentView={currentView} 
          onNavigate={setCurrentView} 
          onViewOwnProfile={handleViewOwnProfile}
          onSignIn={handleSignIn}
          onUserLogin={() => setShowUserLogin(true)}
          user={user}
          commenterUser={commenterUser}
        />
        <Dashboard />
        <Footer 
          onAdminClick={() => setShowAdminLogin(true)}
          onHowItWorksClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('howItWorks')
          }}
          onTermsClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('terms')
          }}
          onPrivacyClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('privacy')
          }}
        />
      </div>
    )
  }

  // Show Explore page
  if (currentView === 'explore') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
        {/* User Login Modal */}
        {showUserLogin && (
          <UserLogin 
            onClose={() => setShowUserLogin(false)} 
            onSuccess={handleUserLoginSuccess}
          />
        )}
        <Header 
          currentView={currentView} 
          onNavigate={setCurrentView} 
          onViewOwnProfile={handleViewOwnProfile}
          onSignIn={handleSignIn}
          onUserLogin={() => setShowUserLogin(true)}
          user={user}
          commenterUser={commenterUser}
        />
        <ExplorePage 
          onProjectSelect={(project) => {
            setSelectedProject(project)
            setCurrentView('projectProfile')
            const urlName = project.projectName.replace(/\s+/g, '')
            window.history.pushState({}, '', `/${urlName}`)
          }}
        />
        <Footer 
          onAdminClick={() => setShowAdminLogin(true)}
          onHowItWorksClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('howItWorks')
          }}
          onTermsClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('terms')
          }}
          onPrivacyClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('privacy')
          }}
        />
      </div>
    )
  }

  // Show Project Profile page
  if (currentView === 'projectProfile' && selectedProject) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
        {/* User Login Modal */}
        {showUserLogin && (
          <UserLogin 
            onClose={() => setShowUserLogin(false)} 
            onSuccess={handleUserLoginSuccess}
          />
        )}
        <Header 
          currentView={currentView} 
          onNavigate={setCurrentView} 
          onViewOwnProfile={handleViewOwnProfile}
          onSignIn={handleSignIn}
          onUserLogin={() => setShowUserLogin(true)}
          user={user}
          commenterUser={commenterUser}
        />
        <ProjectProfilePage 
          project={selectedProject} 
          onBack={() => {
            setCurrentView('explore')
            window.history.pushState({}, '', '/explore')
          }}
          currentUser={user}
          isAdmin={isAdmin}
        />
        <Footer 
          onAdminClick={() => setShowAdminLogin(true)}
          onHowItWorksClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('howItWorks')
          }}
          onTermsClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('terms')
          }}
          onPrivacyClick={() => {
            window.scrollTo(0, 0)
            setCurrentView('privacy')
          }}
        />
      </div>
    )
  }

  // Loading state
  if (authLoading || projectsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
      {/* User Login Modal */}
      {showUserLogin && (
        <UserLogin 
          onClose={() => setShowUserLogin(false)} 
          onSuccess={handleUserLoginSuccess}
        />
      )}

      {/* Header */}
      <Header 
        currentView={currentView} 
        onNavigate={setCurrentView} 
        onViewOwnProfile={handleViewOwnProfile}
        onSignIn={handleSignIn}
        onUserLogin={() => setShowUserLogin(true)}
        user={user}
        commenterUser={commenterUser}
      />

      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 relative">
        {/* LIVE BETA Badge */}
        <div className="absolute top-8 right-8 z-10">
          <div className="relative animate-bounce">
            {/* Rotating glow rings */}
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 blur-2xl opacity-60 animate-spin" style={{ animationDuration: '3s' }}></div>
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-600 blur-xl opacity-50 animate-pulse"></div>
            
            {/* Sparkle effects */}
            <div className="absolute -top-1 -right-1 text-2xl animate-ping">✨</div>
            <div className="absolute -bottom-1 -left-1 text-xl animate-pulse" style={{ animationDelay: '0.5s' }}>⭐</div>
            <div className="absolute top-1/2 -left-2 text-lg animate-bounce" style={{ animationDelay: '0.3s' }}>💫</div>
            
            {/* Badge */}
            <div className="relative px-5 py-2.5 bg-gradient-to-r from-[#FF00FF] via-[#0052FF] to-[#00D9FF] rounded-full border-2 border-white/50 shadow-2xl transform hover:scale-110 transition-transform duration-300">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 rounded-full opacity-0 hover:opacity-30 animate-pulse"></div>
              <span className="relative text-white font-extrabold text-sm tracking-widest drop-shadow-lg animate-pulse" style={{ textShadow: '0 0 10px rgba(255,255,255,0.8), 0 0 20px rgba(0,217,255,0.6)' }}>
                🎉 LIVE BETA 🚀
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Discover the Best
            <span className="block bg-gradient-to-r from-[#0052FF] to-[#60A5FA] bg-clip-text text-transparent">
              NFT & Crypto Projects on Base Blockchain
            </span>
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            Explore featured projects on Base blockchain. Project owners can showcase their NFT collections and crypto ventures to thousands of visitors.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
              <TrendingUp className="w-5 h-5 text-[#60A5FA]" />
              <span className="text-white">Featured Projects</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
              <Search className="w-5 h-5 text-[#60A5FA]" />
              <span className="text-white">Research & Discovery Tool</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
              <DollarSign className="w-5 h-5 text-[#60A5FA]" />
              <span className="text-white">Affordable Marketing</span>
            </div>
          </div>
        </div>
      </section>

      {/* Banner Showcase */}
      <section id="explore" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-3xl font-bold text-white">Featured Projects</h3>
            <span className="text-gray-400">{projects.length} Active Listings</span>
          </div>
          
          {projects.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-gray-400 text-xl">No active projects yet. Be the first to list your project!</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {projects.map((project) => (
                <div
                  key={project._id}
                  className="relative group cursor-pointer"
                  onMouseEnter={() => setHoveredBanner(project._id)}
                  onMouseLeave={() => setHoveredBanner(null)}
                  onClick={() => handleBannerClick(project)}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 transition-all duration-300 hover:border-[#0052FF] hover:shadow-[0_0_30px_rgba(0,82,255,0.3)]">
                    <img
                      src={project.bannerImageUrl}
                      alt={project.projectName}
                      className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* Demo Badge */}
                    {project.isDemo && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-purple-600/90 backdrop-blur-sm text-white rounded-full text-sm font-bold">
                          DEMO
                        </span>
                      </div>
                    )}
                    
                    {/* Project Info Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex justify-between items-end">
                        <div>
                          <h4 className="text-2xl font-bold text-white mb-2">{project.projectName}</h4>
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1 bg-[#0052FF]/80 backdrop-blur-sm text-white text-sm rounded-full">
                              Premium
                            </span>
                          </div>
                        </div>
                        <div
                          className={`flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg transition-all duration-300 ${
                            hoveredBanner === project._id ? 'bg-[#0052FF] translate-x-0' : 'translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0'
                          }`}
                        >
                          <span className="text-white font-medium">Visit Project</span>
                          <ArrowRight className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-white mb-4">Simple Pricing</h3>
            <p className="text-xl text-gray-400">Choose the plan that's right for you</p>
          </div>
          
          {/* User Profile Plans */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">USER PROFILES</h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Free Plan */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-white mb-2">Free</h4>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-white">$0</span>
                  <span className="text-gray-400 text-xl">/forever</span>
                </div>
                <p className="text-gray-300 mb-4">Explore and discover</p>
              </div>
              
              <ul className="space-y-2 text-gray-300 mb-8">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-gray-400 text-xs">✓</span>
                  </div>
                  Browse all projects
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-gray-400 text-xs">✓</span>
                  </div>
                  View project profiles
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-gray-400 text-xs">✓</span>
                  </div>
                  Explore featured listings
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-gray-400 text-xs">✓</span>
                  </div>
                  Discover Base projects
                </li>
              </ul>
              
              <button
                disabled
                className="w-full px-6 py-3 bg-white/10 text-gray-400 rounded-lg font-medium cursor-not-allowed"
              >
                Current Plan
              </button>
            </div>

            {/* Commenter Plan */}
            <div className="bg-gradient-to-br from-[#0052FF]/20 to-[#60A5FA]/20 border-2 border-[#0052FF] rounded-2xl p-8 relative hover:shadow-xl hover:shadow-[#0052FF]/50 transition-all">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="px-4 py-1 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white text-sm font-bold rounded-full">
                  POPULAR
                </span>
              </div>
              
              <div className="text-center mb-6">
                <h4 className="text-3xl font-bold text-white mb-2">Commenter</h4>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-white">$5</span>
                  <span className="text-gray-400 text-xl">/month</span>
                </div>
                <p className="text-gray-300 mb-4">Engage with the community</p>
              </div>
              
              <ul className="space-y-2 text-gray-300 mb-8">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#0052FF]/20 flex items-center justify-center">
                    <span className="text-[#0052FF] text-xs">✓</span>
                  </div>
                  Like photos and videos
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#0052FF]/20 flex items-center justify-center">
                    <span className="text-[#0052FF] text-xs">✓</span>
                  </div>
                  Comment on media
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#0052FF]/20 flex items-center justify-center">
                    <span className="text-[#0052FF] text-xs">✓</span>
                  </div>
                  Review projects with 🚀 ratings
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#0052FF]/20 flex items-center justify-center">
                    <span className="text-[#0052FF] text-xs">✓</span>
                  </div>
                  Cancel anytime
                </li>
              </ul>
              
              <button
                onClick={handleSignIn}
                className="w-full px-8 py-3 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-bold text-lg hover:opacity-90 transition-opacity shadow-lg shadow-[#0052FF]/50"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>

        {/* Listed Projects Plans */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-8 text-center">PROJECTS</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Project Owner Plan */}
            <div className="bg-gradient-to-br from-[#FFD700]/20 to-[#FFA500]/20 border-2 border-[#FFD700] rounded-2xl p-8 hover:shadow-xl hover:shadow-[#FFD700]/50 transition-all">
              <div className="text-center mb-6">
                <h4 className="text-3xl font-bold text-white mb-2">Project Owner</h4>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-white">$20</span>
                  <span className="text-gray-400 text-xl">/month</span>
                </div>
                <p className="text-[#00D9FF] font-semibold mb-1">🎉 1 Month Free Trial</p>
                <p className="text-gray-400 text-sm mb-2">Payment info required • No charge for 30 days</p>
                <p className="text-gray-300 mb-4">Showcase your project</p>
              </div>
              
              <ul className="space-y-2 text-gray-300 mb-8">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <span className="text-[#FFD700] text-xs">✓</span>
                  </div>
                  All Commenter features
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <span className="text-[#FFD700] text-xs">✓</span>
                  </div>
                  Custom banner & profile
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <span className="text-[#FFD700] text-xs">✓</span>
                  </div>
                  Media gallery (photos & videos)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <span className="text-[#FFD700] text-xs">✓</span>
                  </div>
                  Social links & website
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <span className="text-[#FFD700] text-xs">✓</span>
                  </div>
                  News Feed access
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <span className="text-[#FFD700] text-xs">✓</span>
                  </div>
                  Cancel anytime
                </li>
              </ul>
              
              <button
                onClick={handleSignIn}
                className="w-full px-8 py-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black rounded-lg font-bold text-lg hover:opacity-90 transition-opacity shadow-lg shadow-[#FFD700]/50"
              >
                Get Started
              </button>
            </div>

            {/* Featured Project Plan */}
            <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 border-2 border-purple-500 rounded-2xl p-8 relative hover:shadow-xl hover:shadow-purple-500/50 transition-all">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  BOOST VISIBILITY
                </span>
              </div>
              
              <div className="text-center mb-6">
                <h4 className="text-3xl font-bold text-white mb-2">Featured</h4>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-white">$15+</span>
                  <span className="text-gray-400 text-lg">/one-time</span>
                </div>
                <p className="text-gray-300 mb-4">One-time boost</p>
              </div>
              
              <ul className="space-y-2 text-gray-300 mb-8">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="text-purple-400 text-xs">✓</span>
                  </div>
                  Featured badge display
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="text-purple-400 text-xs">✓</span>
                  </div>
                  Priority placement
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="text-purple-400 text-xs">✓</span>
                  </div>
                  Flexible tiers (3-30 days)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="text-purple-400 text-xs">✓</span>
                  </div>
                  Maximum visibility
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="text-purple-400 text-xs">✓</span>
                  </div>
                  Requires project listing
                </li>
              </ul>
              
              {user ? (
                <button
                  onClick={() => {
                    setCurrentView('dashboard')
                    setTimeout(() => {
                      const section = document.getElementById('feature-project-section')
                      if (section) section.scrollIntoView({ behavior: 'smooth' })
                    }, 100)
                  }}
                  className="w-full px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold text-lg hover:opacity-90 transition-opacity shadow-lg shadow-purple-500/50"
                >
                  Feature My Project
                </button>
              ) : (
                <button
                  onClick={handleSignIn}
                  className="w-full px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold text-lg hover:opacity-90 transition-opacity shadow-lg shadow-purple-500/50"
                >
                  Get Started
                </button>
              )}
            </div>
          </div>
        </div>
          
          <div className="text-center mt-8">
            <button
              onClick={() => {
                window.scrollTo(0, 0)
                setCurrentView('pricing')
              }}
              className="text-[#60A5FA] hover:text-white transition-colors underline"
            >
              View detailed pricing comparison →
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer 
        onAdminClick={() => setShowAdminLogin(true)}
        onHowItWorksClick={() => {
          window.scrollTo(0, 0)
          setCurrentView('howItWorks')
        }}
        onTermsClick={() => {
          window.scrollTo(0, 0)
          setCurrentView('terms')
        }}
        onPrivacyClick={() => {
          window.scrollTo(0, 0)
          setCurrentView('privacy')
        }}
      />
    </div>
  )
}

export default App
